import { onMounted, onBeforeUnmount } from 'vue';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '@/stores/auth';
// Module-level singleton — one hub connection per app, multi-subscriber.
let connection = null;
let refCount = 0;
const handlers = new Set();
const notificationHandlers = new Set();
function ensureConnection() {
    if (connection)
        return;
    const auth = useAuth();
    connection = new signalR.HubConnectionBuilder()
        .withUrl('/hubs/dashboard', {
        accessTokenFactory: () => auth.getAccessToken().then((t) => t ?? ''),
    })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();
    connection.on('summaryInvalidated', () => {
        for (const h of handlers)
            h();
    });
    connection.on('notificationFanout', (payload) => {
        for (const h of notificationHandlers)
            h(payload);
    });
    connection.start().catch((err) => {
        // Non-fatal during dev — backend hub may not be deployed yet.
        console.warn('[useSignalRDashboard] connection failed', err);
    });
}
function maybeTearDown() {
    if (refCount === 0 && connection) {
        void connection.stop();
        connection = null;
    }
}
/**
 * Subscribe to dashboard-summary invalidations. Returns an unsubscribe fn.
 * Use directly when not in a component setup; prefer useSignalRDashboard()
 * + onDashboardInvalidated when inside a setup function.
 */
export function onDashboardInvalidated(handler) {
    handlers.add(handler);
    return () => { handlers.delete(handler); };
}
/**
 * Subscribe to notificationFanout events pushed by DashboardHub.
 * Returns an unsubscribe fn. Call from composables, not components.
 */
export function onNotification(handler) {
    notificationHandlers.add(handler);
    return () => { notificationHandlers.delete(handler); };
}
/**
 * Manages the SignalR HubConnection lifecycle for the dashboard.
 *
 * - Builds a module-level singleton connection on first mount.
 * - Tracks refcount; tears down when the last consumer unmounts.
 * - withAutomaticReconnect() uses the library's built-in exponential backoff.
 * - Subscriptions belong to the caller — pass handlers to onDashboardInvalidated(),
 *   not to this function.
 */
export function useSignalRDashboard() {
    onMounted(() => {
        refCount++;
        ensureConnection();
    });
    onBeforeUnmount(() => {
        refCount--;
        maybeTearDown();
    });
}
